import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPacksUnreadSummary } from '../components/documents/packApi';
import { getUnreadMessagesCount } from '../components/chat/chatApi';
import { getTasksUnreadCount } from '../components/tasks/taskApi';
import { getBusinessCompanies, getEmployeeAccessibleCompanies } from '../components/login/loginApi';
import {
    sumBadgeTotals,
    computeChatBadgeCount,
    sumChatMapCounts,
} from '../utils/badgeCounts';

const EMPTY_BY_USER = {};
const EMPTY_BY_COMPANY = {};
const EMPTY_TOTALS = { tasks: 0, documents: 0, chat: 0 };

function mergeByCompanyId(target, source) {
    Object.entries(source || {}).forEach(([companyId, count]) => {
        const id = Number(companyId);
        if (!id) return;
        target[id] = (target[id] || 0) + (Number(count) || 0);
    });
    return target;
}

function mergeMaxById(target, source) {
    Object.entries(source || {}).forEach(([key, count]) => {
        const id = Number(key);
        if (!id) return;
        target[id] = Math.max(target[id] || 0, Number(count) || 0);
    });
    return target;
}

async function fetchDocumentCountsByCompany(businessAccountId, existing = {}) {
    const biz = Number(businessAccountId) || 0;
    if (!biz) return { ...existing };

    const [owned, delegated] = await Promise.all([
        getBusinessCompanies(biz, { inWork: 1 }).catch(() => ({ companies: [] })),
        getEmployeeAccessibleCompanies(biz, 'all', { inWork: 1 }).catch(() => ({ companies: [] })),
    ]);
    const seen = new Set();
    const missing = [];
    [...(owned.companies || []), ...(delegated.companies || [])].forEach((company) => {
        const id = Number(company?.company_id);
        if (!id || seen.has(id)) return;
        seen.add(id);
        if (existing[id] == null) missing.push(id);
    });
    if (!missing.length) return { ...existing };

    const extra = await Promise.all(
        missing.map(async (companyId) => {
            const summary = await getPacksUnreadSummary(biz, { company_id: companyId }).catch(() => ({}));
            return [companyId, Number(summary?.total) || 0];
        })
    );
    return { ...existing, ...Object.fromEntries(extra) };
}

async function fetchBadgesForUser(userId) {
    const [docsSummary, chatSummary, tasksUnread] = await Promise.all([
        getPacksUnreadSummary(userId).catch(() => ({})),
        getUnreadMessagesCount(userId).catch(() => ({})),
        getTasksUnreadCount(userId).catch(() => ({ unread_count: 0, by_company_id: {} })),
    ]);

    const byChatSum = sumChatMapCounts(chatSummary?.by_chat_id);
    const byCompanySum = sumChatMapCounts(chatSummary?.by_company_id);
    // Бейдж = сумма по чатам всех компаний (не orphan-сообщения без chat_id).
    const chatMessages = byChatSum > 0
        ? byChatSum
        : (byCompanySum > 0 ? byCompanySum : (Number(chatSummary?.unread_count) || 0));
    const chat = computeChatBadgeCount(chatMessages, chatSummary?.manual_unread);
    console.log('[web-badge] fetch', {
        userId,
        unread_count: chatSummary?.unread_count,
        manual_unread: chatSummary?.manual_unread,
        badge_count: chatSummary?.badge_count,
        byChatSum,
        byCompanySum,
        chat,
        tasks_unread: tasksUnread?.unread_count,
        tasks_by_company: tasksUnread?.by_company_id,
        by_company: chatSummary?.by_company_id,
        by_chat: chatSummary?.by_chat_id,
    });

    return {
        tasks: Number(tasksUnread?.unread_count) || 0,
        documents: Number(docsSummary?.total) || 0,
        chat,
        hasEmptyBankPack: Boolean(docsSummary?.has_empty_active_bank_pack),
        byCompanyId: docsSummary?.by_company_id && typeof docsSummary.by_company_id === 'object'
            ? docsSummary.by_company_id
            : null,
        tasksByCompanyId: tasksUnread?.by_company_id && typeof tasksUnread.by_company_id === 'object'
            ? tasksUnread.by_company_id
            : {},
        chatByCompanyId: chatSummary?.by_company_id && typeof chatSummary.by_company_id === 'object'
            ? chatSummary.by_company_id
            : null,
        chatByChatId: chatSummary?.by_chat_id && typeof chatSummary.by_chat_id === 'object'
            ? chatSummary.by_chat_id
            : null,
    };
}

export function useAccountBadges(users, options = {}) {
    const {
        enabled = true,
        refreshInterval = 15000,
        refreshKey = 0,
        businessAccountId = null,
    } = options;

    const userIds = useMemo(() => {
        const ids = new Set();
        (users || []).forEach((u) => {
            if (!u || u.is_company_context || u.is_company_record) return;
            const id = Number(u.user_id);
            if (id > 0) ids.add(id);
        });
        const biz = Number(businessAccountId);
        if (biz > 0) ids.add(biz);
        return [...ids];
    }, [users, businessAccountId]);

    const [byUserId, setByUserId] = useState(EMPTY_BY_USER);
    const [byCompanyId, setByCompanyId] = useState(EMPTY_BY_COMPANY);
    const [tasksByCompanyId, setTasksByCompanyId] = useState(EMPTY_BY_COMPANY);
    const [chatByCompanyId, setChatByCompanyId] = useState(EMPTY_BY_COMPANY);
    const [chatByChatId, setChatByChatId] = useState(EMPTY_BY_COMPANY);
    const [totals, setTotals] = useState(EMPTY_TOTALS);
    const [hasEmptyBankPack, setHasEmptyBankPack] = useState(false);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!userIds.length) {
            setByUserId(EMPTY_BY_USER);
            setByCompanyId(EMPTY_BY_COMPANY);
            setTasksByCompanyId(EMPTY_BY_COMPANY);
            setChatByCompanyId(EMPTY_BY_COMPANY);
            setChatByChatId(EMPTY_BY_COMPANY);
            setTotals(EMPTY_TOTALS);
            setHasEmptyBankPack(false);
            return;
        }

        setLoading(true);
        try {
            const entries = await Promise.all(
                userIds.map(async (userId) => {
                    try {
                        const badges = await fetchBadgesForUser(userId);
                        return [userId, badges];
                    } catch (error) {
                        console.warn('account badges failed for', userId, error);
                        return [userId, { tasks: 0, documents: 0, chat: 0, hasEmptyBankPack: false, byCompanyId: null, tasksByCompanyId: null, chatByCompanyId: null, chatByChatId: null }];
                    }
                })
            );

            const nextByUser = Object.fromEntries(entries);
            const nextByCompany = {};
            const nextTasksByCompany = {};
            const nextChatByCompany = {};
            const nextChatByChat = {};
            entries.forEach(([, badges]) => {
                if (badges?.byCompanyId) mergeByCompanyId(nextByCompany, badges.byCompanyId);
                if (badges?.tasksByCompanyId) mergeByCompanyId(nextTasksByCompany, badges.tasksByCompanyId);
            });
            entries.forEach(([fetchedUserId, badges]) => {
                if (businessAccountId && Number(fetchedUserId) !== Number(businessAccountId)) return;
                if (badges?.chatByCompanyId) mergeMaxById(nextChatByCompany, badges.chatByCompanyId);
                if (badges?.chatByChatId) mergeMaxById(nextChatByChat, badges.chatByChatId);
            });
            const byCompany = await fetchDocumentCountsByCompany(businessAccountId, nextByCompany);
            const { totals: nextTotals, hasEmptyBankPack: nextBank } = sumBadgeTotals(nextByUser);
            const chatFromMaps = sumChatMapCounts(nextChatByChat) || sumChatMapCounts(nextChatByCompany);
            if (businessAccountId && nextByUser[businessAccountId]) {
                nextTotals.chat = chatFromMaps > 0
                    ? chatFromMaps
                    : (Number(nextByUser[businessAccountId].chat) || 0);
            } else if (chatFromMaps > 0) {
                nextTotals.chat = chatFromMaps;
            }
            console.log('[web-badge] totals', {
                userIds,
                chat: nextTotals.chat,
                chatFromMaps,
                byUser: nextByUser,
                byCompany,
                tasksByCompany: nextTasksByCompany,
                chatByCompany: nextChatByCompany,
                chatByChat: nextChatByChat,
            });
            setByUserId(nextByUser);
            setByCompanyId(byCompany);
            setTasksByCompanyId(nextTasksByCompany);
            setChatByCompanyId((prev) => {
                const next = {};
                Object.keys(prev || {}).forEach((k) => {
                    const id = Number(k);
                    if (id) next[id] = 0;
                });
                mergeMaxById(next, nextChatByCompany);
                return next;
            });
            setChatByChatId((prev) => {
                const next = {};
                Object.keys(prev || {}).forEach((k) => {
                    const id = Number(k);
                    if (id) next[id] = 0;
                });
                mergeMaxById(next, nextChatByChat);
                return next;
            });
            setTotals(nextTotals);
            setHasEmptyBankPack(nextBank);
        } finally {
            setLoading(false);
        }
    }, [userIds, businessAccountId]);

    const patchChatBadge = useCallback((userId, chatCount, extra = {}) => {
        const uid = Number(userId);
        const chatId = Number(extra.chatId) || 0;
        const companyId = Number(extra.companyId) || 0;
        const byChatId = extra.byChatId && typeof extra.byChatId === 'object' ? extra.byChatId : null;

        if (byChatId) {
            setChatByChatId((prev) => {
                const next = {};
                Object.keys(prev || {}).forEach((k) => {
                    const id = Number(k);
                    if (id) next[id] = 0;
                });
                Object.entries(byChatId).forEach(([key, count]) => {
                    const id = Number(key);
                    if (id) next[id] = Number(count) || 0;
                });
                if (chatId) next[chatId] = 0;
                return next;
            });
        } else if (chatId) {
            setChatByChatId((prev) => ({ ...prev, [chatId]: 0 }));
        }

        if (companyId && extra.companyCount != null) {
            setChatByCompanyId((prev) => ({
                ...prev,
                [companyId]: Number(extra.companyCount) || 0,
            }));
        } else if (companyId && extra.byCompanyId && typeof extra.byCompanyId === 'object') {
            setChatByCompanyId((prev) => ({
                ...prev,
                [companyId]: Number(extra.byCompanyId[companyId]) || 0,
            }));
        }

        if (!uid || chatCount === undefined) return;
        const count = Number(chatCount) || 0;
        setByUserId((prev) => {
            const row = prev[uid] || { tasks: 0, documents: 0, chat: 0, hasEmptyBankPack: false };
            const next = { ...prev, [uid]: { ...row, chat: count } };
            const { totals: nextTotals, hasEmptyBankPack: nextBank } = sumBadgeTotals(next);
            setTotals(nextTotals);
            setHasEmptyBankPack(nextBank);
            return next;
        });
    }, []);

    useEffect(() => {
        if (!enabled || !userIds.length) {
            setByUserId(EMPTY_BY_USER);
            setByCompanyId(EMPTY_BY_COMPANY);
            setTasksByCompanyId(EMPTY_BY_COMPANY);
            setChatByCompanyId(EMPTY_BY_COMPANY);
            setChatByChatId(EMPTY_BY_COMPANY);
            setTotals(EMPTY_TOTALS);
            setHasEmptyBankPack(false);
            return undefined;
        }

        refresh();
        const timer = setInterval(refresh, refreshInterval);
        return () => clearInterval(timer);
    }, [enabled, refresh, refreshInterval, userIds, refreshKey]);

    return {
        byUserId,
        byCompanyId,
        tasksByCompanyId,
        chatByCompanyId,
        chatByChatId,
        totals,
        hasEmptyBankPack,
        loading,
        refresh,
        patchChatBadge,
    };
}
